import { createClient, EntrySkeletonType } from 'contentful';

const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID || '',
  accessToken: process.env.NODE_ENV === 'development' ? process.env.REACT_APP_CONTENTFUL_PREVIEW_TOKEN || '' : process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '',
  host: process.env.NODE_ENV === 'development' ? 'preview.contentful.com' : 'cdn.contentful.com',
});

export interface Article {
  title: string;
  publishDate: string;
  slug: string;
  body: string;
  readingTime: string; // This will be calculated, not from Contentful
}

// Define a type that matches the actual Contentful response structure
interface RichTextMark {
  type: string;
}

interface RichTextNode {
  nodeType: string;
  content?: RichTextNode[];
  value?: string;
  marks?: RichTextMark[];
  data?: Record<string, any>;
}

interface ContentfulRichText {
  nodeType: string;
  content: RichTextNode[];
  data: Record<string, any>;
}

interface ArticleFields extends EntrySkeletonType {
  contentTypeId: 'article';
  fields: {
    title: string;
    slug: string;
    body: ContentfulRichText;
    publishDate: string;
  };
  sys: {
    contentType: {
      sys: {
        id: string;
      };
    };
  };
}

// Helper function to apply marks to text
const applyMarksToText = (text: string, marks: RichTextMark[]): string => {
  if (!marks || marks.length === 0) return text;
  
  // Apply each mark to the text
  return marks.reduce((markedText, mark) => {
    switch (mark.type) {
      case 'bold':
        return `**${markedText}**`;
      case 'italic':
        return `*${markedText}*`;
      case 'underline':
        return `__${markedText}__`;
      case 'code':
        return `\`${markedText}\``;
      case 'superscript':
        return `<sup>${markedText}</sup>`;
      case 'subscript':
        return `<sub>${markedText}</sub>`;
      default:
        return markedText;
    }
  }, text);
};

// Helper function to extract text from Rich Text document
const extractTextFromRichText = (document: ContentfulRichText): string => {
  if (!document || !document.content) return '';
  
  // Extract all text from the document
  let extractedText = '';
  
  // Process each node in the document
  document.content.forEach(node => {
    // Handle different node types
    switch (node.nodeType) {
      case 'paragraph':
        if (node.content) {
          // Extract text from all text nodes in this paragraph
          const paragraphText = node.content
            .filter(textNode => textNode.nodeType === 'text' && textNode.value)
            .map(textNode => {
              // Get the text value and apply marks
              const text = textNode.value || '';
              return textNode.marks ? applyMarksToText(text, textNode.marks) : text;
            })
            .join(' ');
          
          // Add the paragraph text with a newline
          extractedText += paragraphText + '\n\n';
        }
        break;
        
      case 'heading-1':
        if (node.content) {
          const headingText = node.content
            .filter(textNode => textNode.nodeType === 'text' && textNode.value)
            .map(textNode => {
              const text = textNode.value || '';
              return textNode.marks ? applyMarksToText(text, textNode.marks) : text;
            })
            .join(' ');
          
          extractedText += `# ${headingText}\n\n`;
        }
        break;
        
      case 'heading-2':
        if (node.content) {
          const headingText = node.content
            .filter(textNode => textNode.nodeType === 'text' && textNode.value)
            .map(textNode => {
              const text = textNode.value || '';
              return textNode.marks ? applyMarksToText(text, textNode.marks) : text;
            })
            .join(' ');
          
          extractedText += `## ${headingText}\n\n`;
        }
        break;
        
      case 'heading-3':
        if (node.content) {
          const headingText = node.content
            .filter(textNode => textNode.nodeType === 'text' && textNode.value)
            .map(textNode => {
              const text = textNode.value || '';
              return textNode.marks ? applyMarksToText(text, textNode.marks) : text;
            })
            .join(' ');
          
          extractedText += `### ${headingText}\n\n`;
        }
        break;
        
      case 'unordered-list':
        if (node.content) {
          node.content.forEach(listItem => {
            if (listItem.nodeType === 'list-item' && listItem.content) {
              const itemText = listItem.content
                .filter(textNode => textNode.nodeType === 'text' && textNode.value)
                .map(textNode => {
                  const text = textNode.value || '';
                  return textNode.marks ? applyMarksToText(text, textNode.marks) : text;
                })
                .join(' ');
              
              extractedText += `- ${itemText}\n`;
            }
          });
          extractedText += '\n';
        }
        break;
        
      case 'ordered-list':
        if (node.content) {
          let itemCount = 1;
          node.content.forEach(listItem => {
            if (listItem.nodeType === 'list-item' && listItem.content) {
              const itemText = listItem.content
                .filter(textNode => textNode.nodeType === 'text' && textNode.value)
                .map(textNode => {
                  const text = textNode.value || '';
                  return textNode.marks ? applyMarksToText(text, textNode.marks) : text;
                })
                .join(' ');
              
              extractedText += `${itemCount}. ${itemText}\n`;
              itemCount++;
            }
          });
          extractedText += '\n';
        }
        break;
        
      case 'blockquote':
        if (node.content) {
          const quoteText = node.content
            .filter(textNode => textNode.nodeType === 'text' && textNode.value)
            .map(textNode => {
              const text = textNode.value || '';
              return textNode.marks ? applyMarksToText(text, textNode.marks) : text;
            })
            .join(' ');
          
          extractedText += `> ${quoteText}\n\n`;
        }
        break;
        
      case 'hr':
        extractedText += '---\n\n';
        break;
        
      default:
        // For other node types, try to extract text if possible
        if (node.content) {
          const nodeText = node.content
            .filter(textNode => textNode.nodeType === 'text' && textNode.value)
            .map(textNode => {
              const text = textNode.value || '';
              return textNode.marks ? applyMarksToText(text, textNode.marks) : text;
            })
            .join(' ');
          
          if (nodeText) {
            extractedText += nodeText + '\n\n';
          }
        }
    }
  });
  
  return extractedText.trim();
};

// Function to estimate reading time based on text content
const estimateReadingTime = (text: string): string => {
  // Less than average reading speed: 150 words per minute
  const wordsPerMinute = 150;
  
  // Count words (split by whitespace and filter out empty strings)
  const wordCount = text.trim().split(/\s+/).filter(word => word.length > 0).length;
  
  // Calculate minutes (rounded up to nearest minute)
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  
  // Format the reading time
  if (minutes <= 1) {
    return '~ 1 minute to read';
  } else {
    return `~ ${minutes} minutes to read`;
  }
};

export const getArticles = async (): Promise<Article[]> => {
  try {
    const response = await client.getEntries<ArticleFields>({
      content_type: 'article',
      order: ['-sys.createdAt'],
    });

    return response.items.map((item) => {
      const bodyContent = extractTextFromRichText(item.fields.body);
      const readingTime = estimateReadingTime(bodyContent);
      
      return {
        title: String(item.fields.title),
        slug: String(item.fields.slug),
        body: bodyContent,
        publishDate: String(item.fields.publishDate),
        readingTime,
      };
    });
  } catch (error) {
    console.error('Error fetching articles:', error);
    throw error;
  }
};

export const getArticleBySlug = async (slug: string): Promise<Article | null> => {
  try {
    const response = await client.getEntries<ArticleFields>({
      content_type: 'article',
      // @ts-ignore - Contentful's type definitions are incomplete for field queries
      'fields.slug[match]': slug,
      limit: 1,
    });

    if (response.items.length === 0) {
      return null;
    }

    const item = response.items[0];
    const bodyContent = extractTextFromRichText(item.fields.body);
    const readingTime = estimateReadingTime(bodyContent);
    
    return {
      title: item.fields.title,
      slug: item.fields.slug,
      body: bodyContent,
      publishDate: item.fields.publishDate,
      readingTime,
    };
  } catch (error) {
    return null;
  }
};

export default client; 